/**
 * The single message bubble used by every role.
 *
 * Fixes carried in from the audit:
 *  • attachments now actually render (images + downloadable file cards)
 *  • reply-to quotes render and are clickable
 *  • delivery receipts reflect the message's own state instead of a global flag
 *  • timestamps survive Firestore Timestamp values
 *  • sent/received are distinguishable by colour, alignment, tail and label
 *  • message actions are keyboard reachable, not hover-only
 */

import React from 'react';
import { AlertCircle, Check, CheckCheck, Clock, MoreHorizontal, Reply, SmilePlus } from 'lucide-react';
import type { MessageAttachment, MessagingMessage } from './types';
import { AvatarSpacer, MessagingAvatar, MessagingTag } from './primitives';
import { LinkPreview, MessageAttachments } from './Attachments';
import {
  MESSAGE_REACTION_CHOICES,
  firstLink,
  formatFullTimestamp,
  formatMessageTime,
  messageBodyText,
  roleLabel,
  splitLinks,
} from './utils';

export interface MessageBubbleProps {
  message: MessagingMessage;
  isOwn: boolean;
  grouped: boolean;
  lastOfGroup: boolean;
  /** Hide the author name row (1:1 threads where it adds no information). */
  showAuthor?: boolean;
  showAvatar?: boolean;
  highlight?: string;
  onReply?: (message: MessagingMessage) => void;
  onMore?: (message: MessagingMessage, anchor: HTMLElement) => void;
  onRetry?: (message: MessagingMessage) => void;
  /**
   * Toggle a reaction on this message (ticket threads). When provided,
   * existing reactions render as chips under the bubble and a React
   * action opens the picker. The server owns toggle semantics.
   */
  onReact?: (message: MessagingMessage, reaction: string) => void;
  /** The viewer's id, used only to highlight their own reactions. */
  reactionViewerId?: string;
  onPreviewImage?: (attachment: MessageAttachment) => void;
  onQuoteClick?: (quotedId?: string) => void;
  /** Show a "seen" double tick when the counterpart has read it. */
  showReceipts?: boolean;
  animate?: boolean;
}

function HighlightedText({ text, query }: { text: string; query?: string }) {
  const segments = React.useMemo(() => splitLinks(text), [text]);

  const renderWithHighlight = (value: string, keyPrefix: string) => {
    if (!query || !query.trim()) return value;
    const q = query.trim();
    const parts = value.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ? (
        <mark key={`${keyPrefix}-${i}`} style={{ background: 'rgba(250, 204, 21, 0.55)', color: 'inherit', borderRadius: 3 }}>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
      )
    );
  };

  return (
    <>
      {segments.map((segment, i) =>
        segment.href ? (
          <a
            key={`seg-${i}`}
            href={segment.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
          >
            {renderWithHighlight(segment.text, `link-${i}`)}
          </a>
        ) : (
          <React.Fragment key={`seg-${i}`}>{renderWithHighlight(segment.text, `txt-${i}`)}</React.Fragment>
        )
      )}
    </>
  );
}

function DeliveryReceipt({ state }: { state?: MessagingMessage['delivery'] }) {
  if (!state) return null;
  if (state === 'sending') {
    return (
      <span className="msg-meta-receipt" aria-label="Sending">
        <Clock size={11} />
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="msg-meta-receipt" aria-label="Failed to send" style={{ color: 'var(--color-danger, #ef4444)' }}>
        <AlertCircle size={11} />
      </span>
    );
  }
  if (state === 'read') {
    return (
      <span className="msg-meta-receipt msg-meta-receipt--read" aria-label="Seen">
        <CheckCheck size={12} />
      </span>
    );
  }
  if (state === 'delivered') {
    return (
      <span className="msg-meta-receipt" aria-label="Delivered">
        <CheckCheck size={12} />
      </span>
    );
  }
  return (
    <span className="msg-meta-receipt" aria-label="Sent">
      <Check size={12} />
    </span>
  );
}

export const MessageBubble = React.memo(function MessageBubble({
  message,
  isOwn,
  grouped,
  lastOfGroup,
  showAuthor = true,
  showAvatar = true,
  highlight,
  onReply,
  onMore,
  onRetry,
  onReact,
  reactionViewerId,
  onPreviewImage,
  onQuoteClick,
  showReceipts = true,
  animate = false,
}: MessageBubbleProps) {
  const moreRef = React.useRef<HTMLButtonElement>(null);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const attachments = message.attachments || [];
  const bodyText = messageBodyText(message.body);
  const quotedBody = message.quoted ? messageBodyText(message.quoted.body) : '';
  const link = React.useMemo(() => (attachments.length ? null : firstLink(bodyText)), [
    attachments.length,
    bodyText,
  ]);

  /* System / audit entries (ticket lifecycle events — "Status changed to
     Resolved", assignments…) render as a centred chip, never as a chat
     bubble: they have no author side, no actions and no reactions. This
     mirrors the ticket system's AuditChip in the shared design language. */
  if (message.isSystem) {
    const systemTime = formatMessageTime(message.createdAt);
    return (
      <div className="msg-system-row" role="status">
        <span className="msg-system-chip">
          {message.authorName ? <strong>{message.authorName}</strong> : null}
          {message.authorName && bodyText ? ' ' : null}
          <span>{bodyText}</span>
          {systemTime ? <span className="msg-system-time"> · {systemTime}</span> : null}
        </span>
      </div>
    );
  }

  const reactionEntries = Object.entries(message.reactions || {}).filter(
    ([, users]) => Array.isArray(users) && users.length > 0
  );

  const bubbleTone = message.isInternal ? 'msg-bubble--note' : isOwn ? 'msg-bubble--own' : 'msg-bubble--other';
  const stateTone =
    message.delivery === 'sending'
      ? 'msg-bubble--pending'
      : message.delivery === 'failed'
        ? 'msg-bubble--failed'
        : '';

  const authorName = isOwn ? 'You' : message.authorName || roleLabel(message.authorRole) || 'User';
  const timeLabel = formatMessageTime(message.createdAt);
  const fullTime = formatFullTimestamp(message.createdAt);

  return (
    <div
      className={[
        'msg-row',
        isOwn ? 'msg-row--own' : '',
        grouped ? 'msg-row--grouped' : '',
        animate ? 'msg-row--enter' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="listitem"
      aria-label={`${authorName}${timeLabel ? `, ${timeLabel}` : ''}`}
    >
      {!isOwn &&
        showAvatar &&
        (lastOfGroup ? (
          <MessagingAvatar
            name={message.authorName}
            photoUrl={message.authorPhotoUrl}
            size="xs"
            muted={message.isInternal}
          />
        ) : (
          <AvatarSpacer />
        ))}

      <div className="msg-row-content">
        {!grouped && showAuthor && !isOwn && (
          <span className="msg-author">
            {authorName}
            {message.isInternal && (
              <>
                {' '}
                <MessagingTag tone="warning">Internal</MessagingTag>
              </>
            )}
          </span>
        )}

        <div className={`msg-bubble ${bubbleTone} ${stateTone}`.trim()}>
          {message.quoted && (quotedBody || message.quoted.authorName) && (
            <button
              type="button"
              className="msg-quote hover:msg-quote--hover transition-all duration-150"
              onClick={() => onQuoteClick?.(message.quoted?.id)}
              aria-label={`Replying to ${message.quoted.authorName || 'message'}`}
            >
              <span className="msg-quote-author">{message.quoted.authorName || 'Message'}</span>
              <span className="msg-quote-body">{quotedBody || 'Attachment'}</span>
            </button>
          )}

          {bodyText ? (
            <HighlightedText text={bodyText} query={highlight} />
          ) : attachments.length === 0 ? (
            <span style={{ opacity: 0.6 }}>(no content)</span>
          ) : null}

          <MessageAttachments attachments={attachments} onPreviewImage={onPreviewImage} />
          {link && <LinkPreview url={link} />}
        </div>

        {pickerOpen && onReact && (
          <div className="msg-reaction-picker" role="toolbar" aria-label="Reaction picker">
            {MESSAGE_REACTION_CHOICES.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="msg-reaction-choice"
                aria-label={`React with ${emoji}`}
                onClick={() => {
                  setPickerOpen(false);
                  onReact(message, emoji);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {reactionEntries.length > 0 && (
          <div className="msg-reactions" role="group" aria-label="Reactions">
            {reactionEntries.map(([emoji, users]) => {
              const mine = !!reactionViewerId && users.includes(reactionViewerId);
              return (
                <button
                  key={emoji}
                  type="button"
                  className={`msg-reaction-chip${mine ? ' msg-reaction-chip--mine' : ''}`}
                  aria-label={`${emoji} reaction, ${users.length} ${users.length === 1 ? 'person' : 'people'}${mine ? ', including you' : ''}`}
                  aria-pressed={mine}
                  title={users.join(', ')}
                  onClick={onReact ? () => onReact(message, emoji) : undefined}
                >
                  <span aria-hidden="true">{emoji}</span>
                  <span className="msg-reaction-count">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {message.delivery === 'failed' && (
          <div className="msg-retry-row" style={{ marginTop: 2 }}>
            <button
              type="button"
              className="msg-btn-retry"
              onClick={() => onRetry?.(message)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--color-danger, #ef4444)',
                fontSize: '11.5px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Failed to send · Retry
            </button>
          </div>
        )}

        <span className="msg-meta">
          <time dateTime={fullTime} title={fullTime}>
            {timeLabel}
          </time>
          {message.editedAt ? <span>· edited</span> : null}
          {isOwn && showReceipts && <DeliveryReceipt state={message.delivery} />}
        </span>
      </div>

      {(onReply || onMore || onReact) && (
        <div className="msg-row-actions">
          {onReact && (
            <button
              type="button"
              className="msg-icon-btn msg-icon-btn--sm"
              onClick={() => setPickerOpen((open) => !open)}
              aria-label={`React to message from ${authorName}`}
              aria-expanded={pickerOpen}
              title="React"
            >
              <SmilePlus size={14} />
            </button>
          )}
          {onReply && (
            <button
              type="button"
              className="msg-icon-btn msg-icon-btn--sm"
              onClick={() => onReply(message)}
              aria-label={`Reply to ${authorName}`}
              title="Reply"
            >
              <Reply size={14} />
            </button>
          )}
          {onMore && (
            <button
              ref={moreRef}
              type="button"
              className="msg-icon-btn msg-icon-btn--sm"
              onClick={() => moreRef.current && onMore(message, moreRef.current)}
              aria-label={`More actions for message from ${authorName}`}
              title="More"
            >
              <MoreHorizontal size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
});
